/**
 * Preferences Store
 *
 * Manages user preferences in localStorage with type-safe access
 * and change notifications for reactive UI updates
 */

export interface ColumnPreferences {
  visibleColumns: string[];
}

export type ViewId = 'my-requests' | 'my-approvals' | 'all-requests' | 'travel-requests' | 'out-of-pocket';

export interface UserPreferences {
  columns: {
    [key in ViewId]?: ColumnPreferences;
  };
}

type PreferenceChangeCallback = (preferences: UserPreferences) => void;

class PreferencesStore {
  private readonly storageKey = 'user_preferences';
  private preferences: UserPreferences;
  private listeners = new Set<PreferenceChangeCallback>();

  constructor() {
    this.preferences = this.loadPreferences();
  }

  /**
   * Load preferences from localStorage
   */
  private loadPreferences(): UserPreferences {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        return JSON.parse(stored) as UserPreferences;
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
    }

    return {
      columns: {}
    };
  }

  /**
   * Save preferences to localStorage
   */
  private savePreferences(): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.preferences));
      this.notifyListeners();
    } catch (error) {
      console.error('Failed to save preferences:', error);
    }
  }

  /**
   * Get column preferences for a specific view
   */
  getColumnPreferences(viewId: ViewId): ColumnPreferences | undefined {
    return this.preferences.columns[viewId];
  }

  /**
   * Set column preferences for a specific view
   */
  setColumnPreferences(viewId: ViewId, prefs: ColumnPreferences): void {
    if (!this.preferences.columns) {
      this.preferences.columns = {};
    }
    this.preferences.columns[viewId] = prefs;
    this.savePreferences();
  }

  /**
   * Clear column preferences for a specific view (reset to defaults)
   */
  clearColumnPreferences(viewId: ViewId): void {
    if (this.preferences.columns[viewId]) {
      delete this.preferences.columns[viewId];
      this.savePreferences();
    }
  }

  /**
   * Clear all preferences
   */
  clearAllPreferences(): void {
    this.preferences = { columns: {} };
    localStorage.removeItem(this.storageKey);
    this.notifyListeners();
  }

  /**
   * Subscribe to preference changes
   */
  subscribe(callback: PreferenceChangeCallback): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notify all listeners of preference changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(callback => callback(this.preferences));
  }
}

// Export singleton instance
export const preferencesStore = new PreferencesStore();